import { useBookingId } from "hooks/use-booking-id";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useNavigate } from "react-router-dom";
import { Routes } from "routes";
import { useReservation } from "api/api-hooks/use-create-reservation";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";

type BookingNavigateParams = {
    fromRoute: Routes;
    back: boolean;
};

export const buildSearchParams = (
    updates: Record<string, string | number | null | undefined>,
    existingSearch: string
): string => {
    const params = new URLSearchParams(existingSearch);

    for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === undefined) {
            params.delete(key);
        } else {
            params.set(key, String(value));
        }
    }

    return `?${params.toString()}`;
};

export const useBookingNavigate = () => {
    const bookingId = useBookingId();
    const state = useBookingStateContext();
    const {
        guests,
        guestsChildren: children,
        date: dateMoment,
        isActivityBooking,
        isWaitList,
    } = state;
    const date = dateMoment?.format("YYYY-MM-DD") ?? "";
    const navigate = useNavigate();
    const { data: reservation } = useReservation();
    const activateWebChildren =
        useExternalBookingSettings().data?.unitsMetaData?.activateWebChildren;

    const isSimpleBooking = !isActivityBooking;

    // Include menu step if there are menus in the booking and it's not an activity booking
    const includePreorderStep = reservation?.hasMenus() && isSimpleBooking && !isWaitList;

    const bookingNavigate = ({ fromRoute, back = false }: BookingNavigateParams) => {
        let toRoute: Routes = Routes.WIZARD;
        let search: string = location.search;
        let removeBookingId = false;
        let pathname: string = location.pathname;

        let updatedParams: Record<string, string | number | null | undefined> = {
            guests: null,
            children: null,
            date: null,
        };
        if (back) {
            switch (fromRoute) {
                case Routes.WIZARD:
                    if (activateWebChildren && children !== null) {
                        updatedParams = { ...updatedParams, guests };
                    }
                    break;
                case Routes.AVAILABILITY:
                    toRoute = Routes.WIZARD;
                    updatedParams = { ...updatedParams, guests, children };
                    removeBookingId = true;
                    break;
                case Routes.PREORDER:
                    toRoute = Routes.AVAILABILITY;
                    updatedParams = { guests, children, date };
                    removeBookingId = true;
                    break;
                case Routes.FINALIZE:
                    toRoute = Routes.AVAILABILITY;
                    removeBookingId = isWaitList || isSimpleBooking;

                    if (isSimpleBooking) updatedParams = { guests, children, date };

                    if (includePreorderStep) {
                        toRoute = Routes.PREORDER;
                        removeBookingId = false;
                        updatedParams = {};
                    }
                    break;
            }
        }
        search = buildSearchParams(updatedParams, location.search);

        if (fromRoute) {
            pathname = location.pathname.replace(fromRoute, toRoute);
        }

        if (removeBookingId) {
            pathname = pathname.replace(`/${bookingId}`, "");
        }

        void navigate({
            pathname,
            search,
        });
    };

    return { bookingNavigate };
};
